import { useState } from 'react'
import { Box } from '@material-ui/core'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { makeStyles } from '@material-ui/styles'
import colors from '../colors'
import { SPACING } from '../constants'
import { useNavigate } from 'react-router-dom'
import SignupForm from '../components/forms/SignupForm'
import { useAuthStore } from '../stores/auth'

const MARKETING_URL = 'https://stitch.vip'
// Loaded once at module scope, as Stripe recommends - not per render.
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null
// Drop a product screenshot here to fill the right-hand panel. Until it
// exists the panel falls back to plain copy rather than a broken image.
const PREVIEW_IMAGE = '/images/signup-preview.png'

export default function Signup() {
  const styles = useStyles()
  const jwt = useAuthStore((state) => state.jwt)
  const navigate = useNavigate()
  const [previewAvailable, setPreviewAvailable] = useState(true)

  if (jwt) {
    navigate('/')
  }

  return (
    <Box className={styles.page}>
      <Box className={styles.formPane}>
        <Box className={styles.formInner}>
          {/* The navy wordmark - /images/bare_logo.* is white, for the dark nav. */}
          <img
            src="/bare_logo_blue.png"
            alt="Stitch"
            width={96}
            height={64}
            className={styles.logo}
          />

          <Elements stripe={stripePromise}>
            <SignupForm />
          </Elements>

          <p className={styles.legal}>
            By signing up, you agree to our{' '}
            <a className={styles.legalLink} href={`${MARKETING_URL}/terms-of-use`} target="_blank" rel="noreferrer">
              Terms of Use
            </a>{' '}
            and{' '}
            <a className={styles.legalLink} href={`${MARKETING_URL}/privacy-policy`} target="_blank" rel="noreferrer">
              Privacy Policy
            </a>.
          </p>
        </Box>
      </Box>

      <Box className={styles.visualPane}>
        <Box className={styles.visualInner}>
          {/* Plain elements, not Typography: MUI v5's emotion styles override
              @material-ui/styles v4 makeStyles classes, so colors set here
              would silently not apply to a Typography. */}
          <Box className={styles.visualCopy}>
            <h2 className={styles.visualHeadline}>
              Stitch. Seamless.
            </h2>
            <p className={styles.visualBody}>
              Memberships built for breweries.
            </p>
          </Box>

          {previewAvailable && (
            <img
              src={PREVIEW_IMAGE}
              alt="The Stitch management portal"
              className={styles.preview}
              onError={() => setPreviewAvailable(false)}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}

const useStyles = makeStyles({
  page: {
    display: 'flex',
    minHeight: '100vh',
    background: colors.WHITE,
  },
  formPane: {
    display: 'flex',
    justifyContent: 'center',
    flex: '0 0 auto',
    width: 660,
    maxWidth: '100%',
    padding: `${SPACING * 5}px ${SPACING * 4}px`,
    boxSizing: 'border-box',
    '@media (max-width: 1024px)': {
      width: '100%',
    },
  },
  formInner: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: 560,
  },
  logo: {
    alignSelf: 'center',
    marginBottom: SPACING * 3,
  },
  legal: {
    marginTop: SPACING * 4,
    textAlign: 'center',
    fontSize: 12,
    color: colors.GREY_3,
  },
  legalLink: {
    color: colors.GREY_2,
    textDecoration: 'underline',
  },
  visualPane: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    background: colors.STITCH_BLUE,
    padding: `${SPACING * 6}px 0 ${SPACING * 6}px ${SPACING * 7}px`,
    // The form stands on its own; drop the decorative half on smaller screens.
    '@media (max-width: 1024px)': {
      display: 'none',
    },
  },
  visualInner: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  preview: {
    width: '100%',
    minWidth: 760,
    height: 'auto',
    // No radius or shadow here - the device mockup is a transparent PNG and
    // already carries its own; adding ours would frame its bounding box.
  },
  visualCopy: {
    maxWidth: 480,
    marginBottom: SPACING * 4,
  },
  // Primary: solid white, heavier. Secondary: softened white so it recedes
  // without introducing a second hue.
  visualHeadline: {
    margin: 0,
    fontSize: 34,
    fontWeight: 700,
    letterSpacing: '-0.01em',
    lineHeight: 1.2,
    color: colors.WHITE,
  },
  visualBody: {
    margin: `${SPACING * 2}px 0 0`,
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 1.6,
    color: 'rgba(255, 255, 255, 0.72)',
  },
})
